import React, { useState, useEffect, useCallback } from "react";
import Select, { ActionMeta, MultiValue } from "react-select";
import { get } from "../../utils/api";
import { buildIdFilterArray } from "../../utils/common";

interface IOption {
  value: Record<string, any>;
  label: string;
}

interface ModuleFieldsMultiSelectProps {
  properties: {
    mapping: string;
    collection: string;
    collection_filter: Record<string, any>;
    document_object: string;
    primary_id_field: string;
    primary_name_field: string;
  };
  formData: Record<string, any>;
  values?: IOption[];
  onChange: (values: any[]) => void;
}

function ModuleFieldsMultiSelect({
  formData,
  properties,
  values = [],
  onChange,
}: ModuleFieldsMultiSelectProps) {
  const {
    mapping,
    collection,
    collection_filter,
    document_object,
    primary_id_field,
    primary_name_field,
  } = properties;

  const [options, setOptions] = useState<IOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFields = useCallback(async () => {
    setLoading(true);
    setError(null);
    const formatted_collection_filter = collection_filter
      ? buildIdFilterArray(collection_filter, formData)
      : {};

    try {
      const response = await get(`${collection}`, {
        filter: formatted_collection_filter,
      });

      const final_option_data = Array.isArray(response?.data?.data)
        ? (mapping === "collection_documents_object"
          ? response.data.data[0]?.[document_object] || []
          : response.data.data)
        : [];

      const fieldOptions: IOption[] = final_option_data.map((item: any) => ({
        value: {
          [primary_id_field]: item[primary_id_field],
          [primary_name_field]: item[primary_name_field],
        },
        label: item[primary_name_field],
      }));

      setOptions(fieldOptions);
    } catch (error: unknown) {
      console.error("Failed to fetch fields:", error);
      setError("Failed to load options, click to retry");
    } finally {
      setLoading(false);
    }
  }, [
    mapping,
    collection,
    collection_filter,
    document_object,
    primary_id_field,
    primary_name_field,
    formData[Object.keys(collection_filter)[0]],
  ]);

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  useEffect(() => {
    const validValues = values
      .filter(
        (item:any) =>
          item &&
          item[primary_id_field] !== undefined &&
          item[primary_name_field] !== undefined
      )
      .map((item:any) => ({
        value: {
          [primary_id_field]: item[primary_id_field],
          [primary_name_field]: item[primary_name_field],
        },
        label: item[primary_name_field],
      }));
  
    setSelectedOptions(validValues);
  }, [values, primary_id_field, primary_name_field]);

  const handleChange = (
    newValue: MultiValue<IOption>,
    actionMeta: ActionMeta<IOption>
  ) => {
    const selectedValues = newValue.map((option) => option.value);
    console.log(newValue, "newValue");
    setSelectedOptions(newValue as IOption[]);
    console.log(selectedValues, "newValue selectedValues");
    onChange(selectedValues); // Pass only the `value` section to parent
  };

  const handleRetry = () => {
    setError(null);
    fetchFields();
  };

  if (loading) return <p>Loading...</p>;
  if (error) {
    return (
      <div>
        <p>{error}</p>
        <button
          onClick={handleRetry}
          style={{ padding: "8px 16px", cursor: "pointer" }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Select
      isDisabled={collection_filter && !formData[Object.keys(collection_filter)[0]]}
      isMulti
      value={selectedOptions}
      onChange={handleChange}
      options={options}
      getOptionLabel={(option) => option.label}
      getOptionValue={(option) => option.value[primary_id_field]}
      className="basic-multi-select"
      classNamePrefix="select"
    />
  );
}

export default ModuleFieldsMultiSelect;
